import React, { useEffect, useCallback } from "react";
import { Button, Text } from "design_system";
import { ComposedModal, StackedEmptyModal } from "components/Modal";
import {
  StudentEmailSection,
  RoleSelector,
} from "features/auth/components/onboarding";
import {
  VerificationCodeCore,
  CODE_LENGTH,
} from "features/auth/components/VerificationCodeCore";
import {
  useAddEmailVerification,
  AddEmailVerificationStep,
} from "features/auth/hooks/useAddEmailVerification";
import { useModalStack } from "hooks/useModalStack";
import { ModalStackItem, BaseModalProps } from "store/slices/modalStack";
import { useClaimStudentDiscount } from "features/Subscription/hooks/useClaimStudentDiscount";
import useLabels from "hooks/useLabels";
import { isStudentRole } from "features/auth/utils/roleUtils";
import { InfoIcon } from "lucide-react";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";

export const CLAIM_STUDENT_DISCOUNT_MODAL_ID = "claim-student-discount-modal";

// Props interface for type-safe modal opening
export interface ClaimStudentDiscountModalProps extends BaseModalProps {
  actionSource?: MixpanelActionSource;
}

/**
 * Modal for users to claim student discount by selecting their role
 * and verifying their student email.
 *
 * This component is purely presentational - all business logic
 * is handled by the useClaimStudentDiscount hook.
 */
function ClaimStudentDiscountModal() {
  const modal: ModalStackItem = {
    id: CLAIM_STUDENT_DISCOUNT_MODAL_ID,
    blocksScroll: true,
  };

  const { closeModal, modalProps } =
    useModalStack<ClaimStudentDiscountModalProps>(modal);
  const [labels, authLabels] = useLabels(
    "student-discount.claim-discount-modal",
    "auth"
  );

  const {
    // State
    selectedRole,
    studentEmail,
    studentEmailError,
    showVerification,
    isProcessing,
    unverifiedEmail,

    // Handlers
    handleRoleSelect,
    handleEmailChange,
    handleSubmit,
    handleVerificationComplete,
    handleSkipVerification,
    handleChangeEmail,
    resetState,

    // Derived
    needsStudentEmail,
    isSubmitDisabled,
  } = useClaimStudentDiscount(modalProps?.actionSource);

  // Add email verification hook - pass unverified email when in verification
  const {
    step: verificationStep,
    verificationCode,
    loading: verificationLoading,
    error: verificationError,
    setVerificationCode,
    verifyCode,
    resendCode,
  } = useAddEmailVerification(unverifiedEmail || "");

  // Handle verification complete
  useEffect(() => {
    if (
      showVerification &&
      verificationStep === AddEmailVerificationStep.VERIFIED
    ) {
      handleVerificationComplete();
    }
  }, [showVerification, verificationStep, handleVerificationComplete]);

  // Check if verification code is complete
  const isVerificationComplete = verificationCode.length === CODE_LENGTH;

  // Handle modal close - reset state when closing
  const handleClose = useCallback(() => {
    resetState();
    closeModal();
  }, [resetState, closeModal]);

  // Handle submit and close modal for non-students
  const handleSubmitWithClose = async () => {
    await handleSubmit();
    // If non-student role selected, close the modal after updating metadata
    if (selectedRole && !isStudentRole(selectedRole)) {
      handleClose();
    }
  };

  return (
    <StackedEmptyModal modal={modal} onClose={handleClose}>
      <ComposedModal>
        <ComposedModal.Header
          title={
            showVerification
              ? authLabels.headings["verify-edu-email-for-free-month"]
              : labels["role-selection-title"]
          }
          onClose={handleClose}
        />

        <ComposedModal.Body>
          {showVerification ? (
            <div className="flex justify-center w-full">
              <VerificationCodeCore
                value={verificationCode}
                onChange={setVerificationCode}
                onSubmit={verifyCode}
                isLoading={
                  verificationLoading ||
                  verificationStep === AddEmailVerificationStep.VERIFIED ||
                  isProcessing
                }
                error={verificationError}
                email={unverifiedEmail || ""}
                onResend={resendCode}
                onChangeEmail={handleChangeEmail}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-4">
                <RoleSelector
                  selectedRole={selectedRole}
                  onRoleSelect={handleRoleSelect}
                  isDisabled={isProcessing}
                  hideOtherRoleInput={true}
                  centerAlign={false}
                />

                {/* Student email input when needed */}
                {needsStudentEmail && (
                  <StudentEmailSection
                    studentEmail={studentEmail}
                    onEmailChange={handleEmailChange}
                    emailErrorType={studentEmailError}
                    isDisabled={isProcessing}
                    selectedRole={selectedRole}
                  />
                )}

                {/* Warning message for non-student roles */}
                {selectedRole && !isStudentRole(selectedRole) && (
                  <div className="flex items-center gap-2 py-3 px-4 bg-chart-yellow-faint rounded-xl">
                    <InfoIcon
                      size={20}
                      strokeWidth={2.5}
                      className="text-fg-base flex-shrink-0"
                    />
                    <Text size="sm" theme="base" weight="medium">
                      {labels["non-student-warning"]}
                    </Text>
                  </div>
                )}
              </div>
            </div>
          )}
        </ComposedModal.Body>

        {/* Footer with buttons */}
        <ComposedModal.Footer>
          {showVerification ? (
            <div className="flex flex-col gap-3 w-full">
              <Button
                variant="primary"
                onClick={verifyCode}
                isDisabled={
                  !isVerificationComplete ||
                  verificationLoading ||
                  verificationStep === AddEmailVerificationStep.VERIFIED ||
                  isProcessing
                }
                className="w-full"
              >
                {verificationLoading ||
                verificationStep === AddEmailVerificationStep.VERIFIED
                  ? authLabels.buttons.verifying
                  : authLabels.buttons.verify}
              </Button>
              <Button
                variant="tertiary"
                onClick={handleSkipVerification}
                isDisabled={
                  verificationLoading ||
                  verificationStep === AddEmailVerificationStep.VERIFIED ||
                  isProcessing
                }
                className="w-full"
              >
                {authLabels.buttons["cancel"]}
              </Button>
            </div>
          ) : (
            <Button
              variant="primary"
              onClick={handleSubmitWithClose}
              isDisabled={isSubmitDisabled}
              className="w-full"
            >
              {selectedRole && !isStudentRole(selectedRole)
                ? labels["done-button"]
                : labels["submit-button"]}
            </Button>
          )}
        </ComposedModal.Footer>
      </ComposedModal>
    </StackedEmptyModal>
  );
}

export default ClaimStudentDiscountModal;
